import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Control,
  Controller,
  useFieldArray,
  UseFormStateReturn,
  useWatch,
  useFormContext,
} from 'react-hook-form';
import { MdDeleteOutline } from 'react-icons/md';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Input } from '../../tailwindComponents/Input';
import { Select } from '../../tailwindComponents/Select';
import { Pallet, ProductSkusFormValues } from './types';
import { DIMENSION_UNITS, PALLET_OPTIONS, WEIGHT_UNITS } from './constants';
import { FieldZone } from './FieldZone';
import { PalletStackingExplainer } from './PalletStackingExplainer';
import {
  getStandardPalletLengthInMetres,
  getStandardPalletWidthInMetres,
  getStandardPalletWeightInKg,
} from '../client/AddProductForm/utils/pallet';
import {
  DEFAULT_PALLET_TYPE,
  describeTierExcess,
  isStandardPalletType,
} from '../../constants/pallets';

const STANDARD_PALLET_BASE_HEIGHT_M = 0.145;

const PALLET_TYPE_OPTIONS = PALLET_OPTIONS.map((option) => ({
  label: option.label,
  value: option.value,
}));

function toMetres(value: number, unit: string | undefined): number {
  switch (unit) {
    case 'mm':
      return value / 1000;
    case 'cm':
      return value / 100;
    case 'dm':
      return value / 10;
    case 'm':
    default:
      return value;
  }
}

function toKilograms(value: number, unit: string | undefined): number {
  switch (unit) {
    case 'g':
      return value / 1000;
    case 'oz':
      return value * 0.0283495;
    case 'lb':
      return value * 0.453592;
    case 'Kg':
    default:
      return value;
  }
}

const emptyPallet: Pallet = {
  palletId: '',
  type: DEFAULT_PALLET_TYPE,
  customPalletSpecification: '',
  dimensionsUnitOfMeasure: DIMENSION_UNITS[3],
  length: '',
  width: '',
  height: '',
  customPalletWeightUnit: WEIGHT_UNITS[1],
  customPalletWeight: '',
  amountOfSkusInLayer: '',
  amountOfLayersInPallet: '',
};

function PalletRow({
  skuIndex,
  watchedPallets,
  pallet,
  palletIndex,
  showLabels,
  control,
  formState,
  onRemove,
}: {
  skuIndex: number;
  watchedPallets: Pallet[];
  pallet: Pallet;
  palletIndex: number;
  showLabels: boolean;
  control: Control<ProductSkusFormValues>;
  formState: UseFormStateReturn<ProductSkusFormValues>;
  onRemove: () => void;
}) {
  const { getValues, setValue } = useFormContext<ProductSkusFormValues>();

  const palletErrors =
    formState.errors.skus?.[skuIndex]?.palletising?.[palletIndex];
  const declaresItsOwnDimensions = !isStandardPalletType(pallet.type);

  const skusPerLayer = Number(pallet.amountOfSkusInLayer) || 0;
  const layers = Number(pallet?.amountOfLayersInPallet) || 0;

  const unitOfMeasure = useWatch({
    control,
    name: `skus.${skuIndex}.unitOfMeasure`,
  })?.value;
  const isPalletUnit = unitOfMeasure === 'Pallet';
  const productsPerSku =
    Number(getValues(`skus.${skuIndex}.amountOfProductInSku`)) || 1;
  const unitNoun =
    unitOfMeasure && unitOfMeasure.toLowerCase() !== 'other'
      ? unitOfMeasure
      : 'SKU';

  const skuDimUnit = getValues(
    `skus.${skuIndex}.dimensions.dimensionsUnitOfMeasure`
  )?.value;
  const skuWeightUnit = getValues(
    `skus.${skuIndex}.grossWeightUnitOfMeasure`
  )?.value;
  const skuHeightM = toMetres(
    Number(getValues(`skus.${skuIndex}.dimensions.height`)) || 0,
    skuDimUnit
  );
  const skuWeightKg = toKilograms(
    Number(getValues(`skus.${skuIndex}.grossWeight`)) || 0,
    skuWeightUnit
  );

  const isStandard = isStandardPalletType(pallet.type);
  const palletLengthM = isStandard
    ? getStandardPalletLengthInMetres(pallet.type) ?? 0
    : toMetres(
        Number(pallet?.length) || 0,
        pallet.dimensionsUnitOfMeasure?.value
      );
  const palletWidthM = isStandard
    ? getStandardPalletWidthInMetres(pallet.type) ?? 0
    : toMetres(
        Number(pallet?.width) || 0,
        pallet.dimensionsUnitOfMeasure?.value
      );
  const palletBaseHeightM = isStandard
    ? STANDARD_PALLET_BASE_HEIGHT_M
    : toMetres(
        Number(pallet?.height) || 0,
        pallet.dimensionsUnitOfMeasure?.value
      );
  const palletWeightKg = isStandard
    ? getStandardPalletWeightInKg(pallet.type) ?? 0
    : toKilograms(
        Number(pallet?.customPalletWeight) || 0,
        pallet.customPalletWeightUnit?.value
      );

  const unitsPerPallet = skusPerLayer * layers;
  const totalHeight = skuHeightM * layers + palletBaseHeightM;
  const totalWeight = skuWeightKg * unitsPerPallet + palletWeightKg;
  const cube = palletLengthM * palletWidthM * totalHeight;
  const showProducts = productsPerSku > 1;
  const tierExcessMessage = describeTierExcess(
    pallet.type,
    Math.round(totalHeight * 100),
    skuWeightKg * unitsPerPallet
  );

  const perLayerLabel = `${unitNoun}s per layer`;

  return (
    <div className='py-2.5'>
      <div className='flex flex-wrap items-start gap-3'>
        <div className='w-44'>
          <Controller
            name={`skus.${skuIndex}.palletising.${palletIndex}.type`}
            control={control}
            render={({ field }) => (
              <Select
                label={showLabels ? 'Pallet type' : undefined}
                options={PALLET_TYPE_OPTIONS}
                selected={{ label: field.value, value: field.value }}
                setSelected={(option) =>
                  setValue(
                    `skus.${skuIndex}.palletising.${palletIndex}.type`,
                    String(option.value)
                  )
                }
              />
            )}
          />
        </div>
        <div className='w-36'>
          <Controller
            name={`skus.${skuIndex}.palletising.${palletIndex}.amountOfSkusInLayer`}
            control={control}
            render={({ field }) => (
              <Input
                label={showLabels ? perLayerLabel : undefined}
                aria-label={perLayerLabel}
                {...field}
                disabled={isPalletUnit}
                isInvalid={!!palletErrors?.amountOfSkusInLayer}
                validationMessage={
                  palletErrors?.amountOfSkusInLayer?.message as string
                }
              />
            )}
          />
        </div>
        <div className='w-28'>
          <Controller
            name={`skus.${skuIndex}.palletising.${palletIndex}.amountOfLayersInPallet`}
            control={control}
            render={({ field }) => (
              <Input
                label={showLabels ? 'Layers' : undefined}
                aria-label='Layers'
                {...field}
                disabled={isPalletUnit}
                isInvalid={!!palletErrors?.amountOfLayersInPallet}
                validationMessage={
                  palletErrors?.amountOfLayersInPallet?.message as string
                }
              />
            )}
          />
        </div>
        <div className='flex min-w-0 flex-1 flex-col'>
          {showLabels && <div className='h-7' aria-hidden='true' />}
          <div className='flex h-8 items-center justify-between gap-3'>
            <p className='min-w-[14rem] truncate font-inter text-sm text-gray-500'>
              {unitsPerPallet > 0 ? (
                <>
                  ={' '}
                  <span className='font-medium text-gray-700'>
                    {unitsPerPallet.toLocaleString()} {unitNoun}
                    {unitsPerPallet === 1 ? '' : 's'}
                  </span>
                  {showProducts && (
                    <>
                      {' '}
                      · {(
                        unitsPerPallet * productsPerSku
                      ).toLocaleString()}{' '}
                      products
                    </>
                  )}{' '}
                  · {totalHeight.toFixed(2)} m · {totalWeight.toFixed(1)} kg ·{' '}
                  {cube.toFixed(2)} m³
                </>
              ) : (
                'Per-pallet totals appear here'
              )}
            </p>
            {watchedPallets.length > 1 && (
              <button
                type='button'
                onClick={onRemove}
                aria-label={`Remove ${pallet.type} configuration`}
                className='shrink-0 rounded-md p-1.5 text-gray-400 transition hover:bg-red-50 hover:text-red-600'
              >
                <MdDeleteOutline size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
      {declaresItsOwnDimensions && (
        <div className='ml-2 mt-3 flex flex-wrap items-start gap-3 border-l-2 border-gray-200 pl-4'>
          <div className='w-56'>
            <Controller
              name={`skus.${skuIndex}.palletising.${palletIndex}.customPalletSpecification`}
              control={control}
              defaultValue={pallet.customPalletSpecification}
              render={({ field }) => (
                <Input
                  label='Specification'
                  {...field}
                  isInvalid={!!palletErrors?.customPalletSpecification}
                  validationMessage={
                    palletErrors?.customPalletSpecification?.message as string
                  }
                />
              )}
            />
          </div>
          <div className='w-24'>
            <Controller
              name={`skus.${skuIndex}.palletising.${palletIndex}.dimensionsUnitOfMeasure`}
              control={control}
              render={({ field }) => (
                <Select
                  label='Unit'
                  options={DIMENSION_UNITS}
                  selected={field.value ?? null}
                  setSelected={field.onChange}
                />
              )}
            />
          </div>
          <div className='w-24'>
            <Controller
              name={`skus.${skuIndex}.palletising.${palletIndex}.length`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label='Length'
                  isInvalid={!!palletErrors?.length}
                  validationMessage={palletErrors?.length?.message as string}
                />
              )}
            />
          </div>
          <div className='w-24'>
            <Controller
              name={`skus.${skuIndex}.palletising.${palletIndex}.width`}
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  label='Width'
                  isInvalid={!!palletErrors?.width}
                  validationMessage={palletErrors?.width?.message as string}
                />
              )}
            />
          </div>
          <div className='w-24'>
            <Controller
              name={`skus.${skuIndex}.palletising.${palletIndex}.height`}
              control={control}
              render={({ field }) => (
                <Input
                  label='Height'
                  {...field}
                  isInvalid={!!palletErrors?.height}
                  validationMessage={palletErrors?.height?.message as string}
                />
              )}
            />
          </div>
          <div className='w-24'>
            <Controller
              name={`skus.${skuIndex}.palletising.${palletIndex}.customPalletWeightUnit`}
              control={control}
              render={({ field }) => (
                <Select
                  label='Weight unit'
                  options={WEIGHT_UNITS}
                  selected={field.value ?? WEIGHT_UNITS[1]}
                  setSelected={field.onChange}
                />
              )}
            />
          </div>
          <div className='w-28'>
            <Controller
              name={`skus.${skuIndex}.palletising.${palletIndex}.customPalletWeight`}
              control={control}
              render={({ field }) => (
                <Input
                  label='Pallet weight'
                  {...field}
                  isInvalid={!!palletErrors?.customPalletWeight}
                  validationMessage={
                    palletErrors?.customPalletWeight?.message as string
                  }
                />
              )}
            />
          </div>
        </div>
      )}
      {tierExcessMessage && (
        <div className='mt-2 flex items-start gap-2 rounded-md bg-amber-50/60 px-3 py-2'>
          <ExclamationTriangleIcon className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500' />
          <p className='font-inter text-xs text-gray-600'>
            {tierExcessMessage}
          </p>
        </div>
      )}
    </div>
  );
}

export function PalletisingSection({
  skuIndex,
  control,
  formState,
}: {
  skuIndex: number;
  control: Control<ProductSkusFormValues>;
  formState: UseFormStateReturn<ProductSkusFormValues>;
}) {
  const watchedPallets =
    useWatch({
      control,
      name: `skus.${skuIndex}.palletising`,
    }) ?? [];
  const isPalletUnit =
    useWatch({ control, name: `skus.${skuIndex}.unitOfMeasure` })?.value ===
    'Pallet';

  const {
    fields: pallets,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `skus.${skuIndex}.palletising`,
  });

  const appendPalletRow = () =>
    append({
      ...emptyPallet,
      palletId: uuidv4(),
      ...(isPalletUnit
        ? { amountOfSkusInLayer: '1', amountOfLayersInPallet: '1' }
        : {}),
    });

  const hasAutoSeededPallet = useRef(false);
  useEffect(() => {
    if (pallets.length > 0 || hasAutoSeededPallet.current) return;
    hasAutoSeededPallet.current = true;
    appendPalletRow();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pallets.length, append]);

  return (
    <>
      <PalletStackingExplainer />
      <FieldZone
        title='Pallet stacking'
        hint={
          isPalletUnit
            ? 'A Pallet SKU is a single pallet — fixed at 1 per layer × 1 layer.'
            : 'How this SKU stacks on a pallet — add a configuration for each way it is palletised (arriving or despatching).'
        }
      >
        <div>
          <div className='divide-y divide-gray-200/70'>
            {watchedPallets.map((pallet, renderIndex) => {
              const fieldIndex = pallets.findIndex(
                (p) => p.palletId === pallet.palletId
              );
              return (
                <PalletRow
                  skuIndex={skuIndex}
                  watchedPallets={watchedPallets}
                  key={pallet.palletId}
                  pallet={pallet}
                  palletIndex={fieldIndex}
                  showLabels={renderIndex === 0}
                  control={control}
                  formState={formState}
                  onRemove={() => remove(fieldIndex)}
                />
              );
            })}
          </div>
          <button
            type='button'
            onClick={appendPalletRow}
            className='mt-1 font-inter text-sm font-medium text-primary hover:underline'
          >
            + Add pallet configuration
          </button>
        </div>
      </FieldZone>
    </>
  );
}
